import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, BadgeCheck, Lock, AlertTriangle } from "lucide-react";
import { Screen, ScrollArea, TopBar, BackButton, TextField, PrimaryButton } from "@/components/jk/ui";
import { Card, Pill, SectionLabel, Spinner } from "@/components/jk/kit";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMenu } from "@/context/MenuContext";

const StatusPill = ({ verified }) =>
  verified ? (
    <Pill tone="dark"><BadgeCheck size={13} className="mr-1" /> Verified</Pill>
  ) : (
    <Pill tone="periwinkle">Pending</Pill>
  );

const Kyc = () => {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const { open: openMenu } = useMenu();
  const backToMenu = () => { openMenu(); navigate(-1); };
  const [loading, setLoading] = useState(true);
  const [kyc, setKyc] = useState(null);

  const [aadhaar, setAadhaar] = useState("");
  const [consent, setConsent] = useState(false);
  const [aadhaarBusy, setAadhaarBusy] = useState(false);

  const [pan, setPan] = useState("");
  const [panBusy, setPanBusy] = useState(false);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/astrologer/kyc");
      setKyc(data);
    } catch {
      toast.error("Couldn't load verification status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const aadhaarVerified = kyc?.aadhaar?.status === "verified";
  const panVerified = kyc?.pan?.status === "verified";
  const nameMismatch = panVerified && !kyc?.name_match;

  const verifyAadhaar = async () => {
    if (!/^\d{12}$/.test(aadhaar.replace(/\s/g, ""))) {
      toast.error("Enter a valid 12-digit Aadhaar number");
      return;
    }
    if (!consent) {
      toast.error("Please tick the consent box to continue");
      return;
    }
    setAadhaarBusy(true);
    try {
      const { data } = await api.post("/astrologer/kyc/aadhaar/verify", {
        aadhaar_number: aadhaar.replace(/\s/g, ""), consent,
      });
      setKyc(data);
      setAadhaar(""); setConsent(false);
      await refresh();
      toast.success("Aadhaar verified");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aadhaar verification failed");
    } finally {
      setAadhaarBusy(false);
    }
  };

  const verifyPan = async () => {
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan.toUpperCase().trim())) {
      toast.error("Enter a valid PAN (e.g. ABCDE1234F)");
      return;
    }
    setPanBusy(true);
    try {
      const { data } = await api.post("/astrologer/kyc/pan", { pan_number: pan.toUpperCase().trim() });
      setKyc(data);
      setPan("");
      await refresh();
      if (data.pan?.status === "verified" && !data.name_match) {
        toast.warning("PAN verified, but the name doesn't match your profile");
      } else {
        toast.success("PAN verified");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PAN verification failed");
    } finally {
      setPanBusy(false);
    }
  };

  return (
    <Screen testid="kyc-screen">
      <TopBar left={<BackButton onClick={backToMenu} />} center={<span className="text-[15px] font-semibold">Verification</span>} />
      <ScrollArea className="px-6 pt-2">
        {loading ? (
          <div className="flex justify-center pt-20"><Spinner /></div>
        ) : (
          <>
            <Card dark className="mt-2" data-testid="kyc-banner">
              <SectionLabel className="text-white/60" icon={<ShieldCheck size={13} />}>Why verify?</SectionLabel>
              <p className="mt-2 text-[14px] leading-relaxed text-white/90">
                We verify your Aadhaar and PAN before releasing consultation payouts. Your full Aadhaar
                number is never stored — only a masked copy. Payouts unlock once both are verified and your
                PAN name matches your profile.
              </p>
              <div className="mt-3">
                {kyc?.payout_eligible
                  ? <Pill tone="light"><BadgeCheck size={13} className="mr-1" /> Payouts enabled</Pill>
                  : <Pill tone="peach"><Lock size={13} className="mr-1" /> Payouts on hold</Pill>}
              </div>
            </Card>

            {/* Aadhaar */}
            <div className="mt-6">
              <div className="mb-2 flex items-center justify-between">
                <SectionLabel>Aadhaar</SectionLabel>
                <StatusPill verified={aadhaarVerified} />
              </div>
              <Card>
                {aadhaarVerified ? (
                  <div data-testid="kyc-aadhaar-done">
                    <p className="text-[14px] text-[#111111]">{kyc?.aadhaar?.masked}</p>
                    <p className="mt-1 text-[12px] text-[#666]">
                      Verified genuine{kyc?.aadhaar?.state ? ` · ${kyc.aadhaar.state}` : ""}
                      {kyc?.aadhaar?.gender ? ` · ${kyc.aadhaar.gender}` : ""}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <TextField label="Aadhaar number" testid="kyc-aadhaar-input" inputMode="numeric"
                      maxLength={12} placeholder="12-digit Aadhaar" value={aadhaar}
                      onChange={(e) => setAadhaar(e.target.value.replace(/\D/g, ""))} />
                    <label className="flex items-start gap-2.5 text-[13px] leading-snug text-[#444]" data-testid="kyc-consent">
                      <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                        className="mt-0.5 h-4 w-4 accent-[#111111]" data-testid="kyc-consent-checkbox" />
                      I consent to verify my Aadhaar for payout compliance.
                    </label>
                    <PrimaryButton testid="kyc-verify-aadhaar" onClick={verifyAadhaar} disabled={aadhaarBusy}>
                      {aadhaarBusy ? "Verifying…" : "Verify Aadhaar"}
                    </PrimaryButton>
                  </div>
                )}
              </Card>
            </div>

            {/* PAN */}
            <div className="mt-6 pb-8">
              <div className="mb-2 flex items-center justify-between">
                <SectionLabel>PAN</SectionLabel>
                <StatusPill verified={panVerified} />
              </div>
              <Card>
                {panVerified ? (
                  <div data-testid="kyc-pan-done">
                    <p className="text-[14px] text-[#111111]">
                      {kyc?.pan?.name ? `${kyc.pan.name} · ` : ""}{kyc?.pan?.masked}
                    </p>
                    {nameMismatch && (
                      <div className="mt-3 flex items-start gap-2 rounded-[10px] bg-[#fdf2e6] px-3 py-2.5 text-[12px] leading-snug text-[#8a5a1a]" data-testid="kyc-name-mismatch">
                        <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                        PAN name doesn't match your profile name. Payouts stay on hold — update your profile
                        name to match your PAN, then save it.
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <TextField label="PAN number" testid="kyc-pan-input" maxLength={10} placeholder="ABCDE1234F"
                      value={pan} onChange={(e) => setPan(e.target.value.toUpperCase())} />
                    <PrimaryButton testid="kyc-verify-pan" onClick={verifyPan} disabled={panBusy}>
                      {panBusy ? "Verifying…" : "Verify PAN"}
                    </PrimaryButton>
                  </div>
                )}
              </Card>
            </div>
          </>
        )}
      </ScrollArea>
    </Screen>
  );
};

export default Kyc;
